import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '../../Layouts/Navigation/Navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCar, 
  faMotorcycle,
  faPlus,
  faTrash,
  faCheckCircle,
  faTimesCircle,
  faPencil,
  faCheck,
  faXmark,
  faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../../utils/constants';

const AdminVisitorParking = () => {
  const [spaces, setSpaces] = useState([]);
  const [stats, setStats] = useState({
    carros: { total: 0, disponibles: 0, ocupados: 0 },
    motos: { total: 0, disponibles: 0, ocupados: 0 }
  });
  const [formData, setFormData] = useState({
    numero_espacio: '',
    tipo_vehiculo: 'CARRO'
  });
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [tableNotExists, setTableNotExists] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState({ id: null, numero: '' });
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    fetchSpaces();
    fetchStats();
  }, []);

  const fetchSpaces = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/visitorParking/spaces`);
      setSpaces(response.data);
      setTableNotExists(false);
    } catch (error) {
      console.error('Error al obtener espacios:', error);
      if (error.response?.status === 500 || error.response?.data?.length === 0) {
        setTableNotExists(true);
      }
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/visitorParking/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error al obtener estadísticas:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.numero_espacio.trim()) {
      showSnackbar('Por favor ingresa el número del espacio', 'error');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${BASE_URL}/visitorParking/spaces`, formData);
      
      showSnackbar('Espacio agregado exitosamente', 'success');
      setFormData({
        numero_espacio: '',
        tipo_vehiculo: 'CARRO'
      });
      
      await fetchSpaces();
      await fetchStats();
    } catch (error) {
      console.error('Error al agregar espacio:', error);
      const errorMessage = error.response?.data?.error || 'Error al agregar espacio';
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (space) => {
    setEditingId(space.id_espacio_visitante);
    setEditingData({
      numero_espacio: space.numero_espacio,
      tipo_vehiculo: space.tipo_vehiculo
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData({});
  };

  const handleSaveEdit = async (id) => {
    try {
      await axios.put(`${BASE_URL}/visitorParking/spaces/${id}`, editingData);
      
      showSnackbar('Espacio actualizado exitosamente', 'success');
      setEditingId(null);
      setEditingData({});
      
      await fetchSpaces();
      await fetchStats();
    } catch (error) {
      console.error('Error al actualizar espacio:', error);
      const errorMessage = error.response?.data?.error || 'Error al actualizar espacio';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDelete = (id, numero) => {
    setConfirmTarget({ id, numero });
    setConfirmOpen(true);
  };

  const performDelete = async () => {
    const { id, numero } = confirmTarget;
    setConfirmLoading(true);
    try {
      await axios.delete(`${BASE_URL}/visitorParking/spaces/${id}`);
      showSnackbar(`Espacio ${numero} eliminado exitosamente`, 'success');
      setConfirmOpen(false);
      setConfirmTarget({ id: null, numero: '' });
      await fetchSpaces();
      await fetchStats();
    } catch (error) {
      console.error('Error al eliminar espacio:', error);
      showSnackbar('Error al eliminar espacio', 'error');
    } finally {
      setConfirmLoading(false);
    }
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setConfirmTarget({ id: null, numero: '' });
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => {
      setSnackbar({ open: false, message: '', severity: 'success' });
    }, 3000);
  };

  const totalSpaces = stats.carros.total + stats.motos.total;

  if (tableNotExists) {
    return (
      <>
        <Navigation />
        <div className="p-6">
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <FontAwesomeIcon icon={faTimesCircle} className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Tabla de base de datos no encontrada
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>La tabla <code className="bg-red-100 px-1 rounded">espacio_parqueadero_visitante</code> no existe.</p>
                  <p className="mt-2">Por favor, ejecuta el siguiente script SQL en tu base de datos:</p>
                  <pre className="mt-2 bg-red-100 p-3 rounded text-xs overflow-x-auto">
{`CREATE TABLE IF NOT EXISTS espacio_parqueadero_visitante (
    id_espacio_visitante INT PRIMARY KEY AUTO_INCREMENT,
    numero_espacio VARCHAR(20) NOT NULL UNIQUE,
    tipo_vehiculo ENUM('CARRO', 'MOTO') NOT NULL,
    disponible BOOLEAN DEFAULT TRUE,
    fecha_creacion TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    fecha_actualizacion TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
    INDEX idx_tipo (tipo_vehiculo),
    INDEX idx_disponible (disponible)
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;`}
                  </pre>
                  <p className="mt-2">El archivo SQL se encuentra en: <code className="bg-red-100 px-1 rounded">Backend/src/database/visitor_parking_spaces.sql</code></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Parqueaderos para Visitantes
          </h1>
          <p className="text-gray-600">
            Gestiona los espacios de parqueadero asignados para visitantes
          </p>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Carros */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Espacios Carros</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{stats.carros.total}</p>
                <p className="text-sm text-gray-500 mt-1">
                  <span className="text-green-600 font-semibold">{stats.carros.disponibles} disponibles</span>
                  {stats.carros.ocupados > 0 && (
                    <span className="text-red-600 ml-2">• {stats.carros.ocupados} ocupados</span>
                  )}
                </p>
              </div>
              <div className="bg-blue-100 rounded-full p-3">
                <FontAwesomeIcon icon={faCar} className="h-8 w-8 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Motos */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Espacios Motos</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{stats.motos.total}</p>
                <p className="text-sm text-gray-500 mt-1">
                  <span className="text-green-600 font-semibold">{stats.motos.disponibles} disponibles</span>
                  {stats.motos.ocupados > 0 && (
                    <span className="text-red-600 ml-2">• {stats.motos.ocupados} ocupados</span>
                  )}
                </p>
              </div>
              <div className="bg-green-100 rounded-full p-3">
                <FontAwesomeIcon icon={faMotorcycle} className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </div>

          {/* Total */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Espacios</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{totalSpaces}</p>
                <p className="text-sm text-gray-500 mt-1">
                  <span className="text-green-600 font-semibold">
                    {stats.carros.disponibles + stats.motos.disponibles} disponibles
                  </span>
                </p>
              </div>
              <div className="bg-purple-100 rounded-full p-3">
                <FontAwesomeIcon icon={faLayerGroup} className="h-8 w-8 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Formulario */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Agregar Nuevo Espacio
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número del Espacio *
                </label>
                <input
                  type="text"
                  name="numero_espacio"
                  value={formData.numero_espacio}
                  onChange={handleInputChange}
                  placeholder="Ej: V-101, VISIT-A1, etc."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Vehículo *
                </label>
                <select
                  name="tipo_vehiculo"
                  value={formData.tipo_vehiculo}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="CARRO">Carro</option>
                  <option value="MOTO">Moto</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-6 py-2 rounded-lg hover:from-purple-600 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faPlus} className="h-5 w-5" />
                {isLoading ? 'Agregando...' : 'Agregar Espacio'}
              </button>
            </div>
          </form>
        </div>

        {/* Tablas de Espacios */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tabla de Carros */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <FontAwesomeIcon icon={faCar} className="h-6 w-6" />
                Espacios para Carros ({spaces.filter(s => s.tipo_vehiculo === 'CARRO').length})
              </h2>
            </div>

            {spaces.filter(s => s.tipo_vehiculo === 'CARRO').length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FontAwesomeIcon icon={faCar} className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                <p>No hay espacios para carros</p>
                <p className="text-sm mt-2">Agrega espacios usando el formulario anterior</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Número
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {spaces.filter(s => s.tipo_vehiculo === 'CARRO').map((space) => (
                      <tr key={space.id_espacio_visitante} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          {editingId === space.id_espacio_visitante ? (
                            <input
                              type="text"
                              value={editingData.numero_espacio}
                              onChange={(e) => setEditingData({ ...editingData, numero_espacio: e.target.value })}
                              className="px-2 py-1 border border-gray-300 rounded w-full"
                            />
                          ) : (
                            <span className="text-sm font-bold text-gray-900">
                              {space.numero_espacio}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            space.disponible
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {space.disponible ? 'Disponible' : 'Ocupado'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          {editingId === space.id_espacio_visitante ? (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleSaveEdit(space.id_espacio_visitante)}
                                className="text-green-600 hover:text-green-800"
                                title="Guardar"
                              >
                                <FontAwesomeIcon icon={faCheck} className="h-5 w-5" />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-gray-600 hover:text-gray-800"
                                title="Cancelar"
                              >
                                <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleEdit(space)}
                                className="text-blue-600 hover:text-blue-800"
                                title="Editar"
                              >
                                <FontAwesomeIcon icon={faPencil} className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(space.id_espacio_visitante, space.numero_espacio)}
                                className="text-red-600 hover:text-red-800"
                                title="Eliminar"
                              >
                                <FontAwesomeIcon icon={faTrash} className="h-5 w-5" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Tabla de Motos */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-green-500 to-green-600">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <FontAwesomeIcon icon={faMotorcycle} className="h-6 w-6" />
                Espacios para Motos ({spaces.filter(s => s.tipo_vehiculo === 'MOTO').length})
              </h2>
            </div>

            {spaces.filter(s => s.tipo_vehiculo === 'MOTO').length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FontAwesomeIcon icon={faMotorcycle} className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                <p>No hay espacios para motos</p>
                <p className="text-sm mt-2">Agrega espacios usando el formulario anterior</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Número
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {spaces.filter(s => s.tipo_vehiculo === 'MOTO').map((space) => (
                      <tr key={space.id_espacio_visitante} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          {editingId === space.id_espacio_visitante ? (
                            <input
                              type="text"
                              value={editingData.numero_espacio}
                              onChange={(e) => setEditingData({ ...editingData, numero_espacio: e.target.value })}
                              className="px-2 py-1 border border-gray-300 rounded w-full"
                            />
                          ) : (
                            <span className="text-sm font-bold text-gray-900">
                              {space.numero_espacio}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            space.disponible
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {space.disponible ? 'Disponible' : 'Ocupado'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          {editingId === space.id_espacio_visitante ? (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleSaveEdit(space.id_espacio_visitante)}
                                className="text-green-600 hover:text-green-800"
                                title="Guardar"
                              >
                                <FontAwesomeIcon icon={faCheck} className="h-5 w-5" />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-gray-600 hover:text-gray-800"
                                title="Cancelar"
                              >
                                <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleEdit(space)}
                                className="text-blue-600 hover:text-blue-800"
                                title="Editar"
                              >
                                <FontAwesomeIcon icon={faPencil} className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(space.id_espacio_visitante, space.numero_espacio)}
                                className="text-red-600 hover:text-red-800"
                                title="Eliminar"
                              >
                                <FontAwesomeIcon icon={faTrash} className="h-5 w-5" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Confirm Delete Modal */}
        {confirmOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black opacity-40" onClick={cancelDelete} />
            <div className="bg-white rounded-lg shadow-lg z-10 w-full max-w-md mx-4">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-800">Confirmar Eliminación</h3>
                <p className="text-sm text-gray-600 mt-2">¿Estás seguro de eliminar el espacio <span className="font-medium">{confirmTarget.numero}</span>?</p>
                <div className="mt-4 flex justify-end gap-3">
                  <button onClick={cancelDelete} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">Cancelar</button>
                  <button onClick={performDelete} disabled={confirmLoading} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50">
                    {confirmLoading ? 'Eliminando...' : 'Eliminar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Snackbar */}
        {snackbar.open && (
          <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
            <div className={`flex items-center gap-3 px-6 py-3 rounded-lg shadow-lg ${
              snackbar.severity === 'success'
                ? 'bg-green-500 text-white'
                : 'bg-red-500 text-white'
            }`}>
              {snackbar.severity === 'success' ? (
                <FontAwesomeIcon icon={faCheckCircle} className="h-6 w-6" />
              ) : (
                <FontAwesomeIcon icon={faTimesCircle} className="h-6 w-6" />
              )}
              <span className="font-medium">{snackbar.message}</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminVisitorParking;

